import type { ReactNode } from 'react'

import CopyIcon from '@/assets/icons/copy.svg'
import { Button, Trans } from '@/components'
import { Machine } from '@/models/machine'
import type { CreateInstanceBoundNATInfo } from '@/pages/create-instance/types'

import { SelectMachineTooltip } from './SelectMachineTooltip'

type BindPublicIPSectionProps = {
  machine: Machine | undefined
  boundNAT: CreateInstanceBoundNATInfo | undefined
  setBindNATModalOpen: (open: boolean) => void
  setBoundNAT: (nat: CreateInstanceBoundNATInfo | undefined) => void
}

function InfoItem({
  title,
  children,
  className,
}: {
  title: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <div className={`min-w-[177px] flex-1 ${className ?? ''}`}>
      <div className="text-secondary t-xs mb-1">{title}</div>
      <div className="text-primary t-sb flex items-center gap-1">{children}</div>
    </div>
  )
}

export function BindPublicIPSection({
  machine,
  boundNAT,
  setBindNATModalOpen,
  setBoundNAT,
}: BindPublicIPSectionProps) {
  return (
    <div className="mt-8 px-8 pb-[92px]">
      <div className="t-sb text-secondary mb-2">
        <Trans i18nKey="create.bindNATTitle">Bind Public IP Address</Trans>
      </div>

      {boundNAT ? (
        /* ------- bound public ip ------- */
        <div className="bg-panel-white border border-gray100 rounded-lg p-4 shadow-card-100">
          <div className="flex flex-wrap gap-4">
            <InfoItem title={<Trans i18nKey="create.bindPublicIpTableResId">ID</Trans>}>
              {boundNAT.resID}
            </InfoItem>
            <InfoItem title={<Trans i18nKey="create.bindPublicIpTableIp">Public IP Address</Trans>}>
              <>
                {boundNAT.name}
                <img
                  src={CopyIcon}
                  alt="copy"
                  className="w-4 h-4 cursor-pointer"
                  onClick={() => navigator.clipboard.writeText(boundNAT.name)}
                />
              </>
            </InfoItem>
            <InfoItem title={<Trans i18nKey="create.bindNATModal.protocol">Protocol</Trans>}>
              <Trans i18nKey="create.bindNATModal.customTCP">Custom TCP</Trans>
            </InfoItem>
            <InfoItem
              title={<Trans i18nKey="create.bindNATModal.destinationPort">Destination Port</Trans>}
            >
              {boundNAT.destinationPorts?.join(', ') || '-'}
            </InfoItem>
            <div className="min-w-[177px] flex-1 opacity-0" aria-hidden="true" />
          </div>

          {/* action buttons */}
          <div className="mt-4 border-t border-gray100 pt-3">
            <div className="flex gap-4 t-sb">
              <button
                type="button"
                className="text-btn-secondary cursor-pointer"
                onClick={() => setBindNATModalOpen(true)}
              >
                <Trans i18nKey="create.bindNATEdit">Edit</Trans>
              </button>
              <button
                type="button"
                className="text-red-500 cursor-pointer"
                onClick={() => setBoundNAT(undefined)}
              >
                <Trans i18nKey="create.bindNATUnbind">Unbind</Trans>
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* ------- not bound public ip ------- */
        <SelectMachineTooltip enabled={!machine}>
          <Button type="outline" onClick={() => setBindNATModalOpen(true)} disabled={!machine}>
            <Trans i18nKey="create.bindNATBtn">Bind</Trans>
          </Button>
        </SelectMachineTooltip>
      )}
    </div>
  )
}
